import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("em_site_token") || "");
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("em_admin_token") || "");
  const [authReady, setAuthReady] = useState(false);

  // Verify token on mount
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setAuthReady(true);
        return;
      }
      try {
        await api.get("/auth/verify");
      } catch (_e) {
        if (!cancelled) {
          localStorage.removeItem("em_site_token");
          setToken("");
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (password) => {
    const res = await api.post("/auth/site-login", { password });
    const t = res.data.token;
    localStorage.setItem("em_site_token", t);
    setToken(t);
    return true;
  }, [setToken]);

  const logout = useCallback(() => {
    localStorage.removeItem("em_site_token");
    setToken("");
  }, [setToken]);

  const adminLogin = useCallback(async (password) => {
    const res = await api.post("/admin/login", { password });
    const t = res.data.token;
    localStorage.setItem("em_admin_token", t);
    setAdminToken(t);
    return true;
  }, [setAdminToken]);

  const adminLogout = useCallback(() => {
    localStorage.removeItem("em_admin_token");
    setAdminToken("");
  }, [setAdminToken]);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthed: Boolean(token),
        authReady,
        login,
        logout,
        adminToken,
        isAdmin: Boolean(adminToken),
        adminLogin,
        adminLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
